/******/ (() => { // webpackBootstrap
/**
 * Sticky Add to Cart Component
 * A custom element for handling sticky add to cart functionality
 */
class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    
    this.observer = null;
    this.footerObserver = null;
    this.productSection = null;
    this.footerWrapper = null;
    this.hideTimeout = null;
    this.shouldHideForProduct = false;
    this.shouldHideForFooter = false;
  }

  connectedCallback() {
    // Component has been added to the DOM
    this.init();
    // JL: re-init when switching between mobile and desktop layout
    if (!this.jlMq && window.matchMedia) {
      this.jlMq = window.matchMedia('(max-width: 767px)');
      this.jlOnMq = () => { this.cleanup(); this.init(); };
      if (this.jlMq.addEventListener) this.jlMq.addEventListener('change', this.jlOnMq);
    }
  }

  disconnectedCallback() {
    // Component has been removed from the DOM
    if (this.jlMq && this.jlMq.removeEventListener) this.jlMq.removeEventListener('change', this.jlOnMq);
    this.jlMq = null;
    this.cleanup();
  }

  init() {
    // Find the product section element
    this.productSection = document.querySelector('.product-section');
    
    if (!this.productSection) {
      console.warn('StickyAddToCart: .product-section element not found');
      return;
    }

    // Find the footer wrapper element
    this.footerWrapper = document.querySelector('.footer-wrapper');
    
    if (!this.footerWrapper) {
      console.warn('StickyAddToCart: .footer-wrapper element not found');
    }

    setTimeout(() => {
      // Remove transform on product section to allow position fixed to work
      this.productSection.style.animationName = 'none';
      // Ensure the product section has animated in
      this.productSection.classList.remove('show-on-scroll');
      this.productSection.classList.add('animated', 'fadeIn', 'shown-on-scroll');
    }, 1000);

    // JL: on mobile show the bar as soon as the main add-to-cart button
    // (its stable buttons group, not the sticky copy) has scrolled above the viewport
    this.observedEl = this.productSection;
    this.jlMobileTrigger = false;
    if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
      const mainButtons = Array.prototype.filter.call(
        this.productSection.querySelectorAll('.paymentButtonsWrapper .product-form__buttons-group'),
        (el) => !el.closest('sticky-add-to-cart')
      );
      if (mainButtons.length) {
        this.observedEl = mainButtons[0];
        this.jlMobileTrigger = true;
      }
    }

    // Create Intersection Observer for product section
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Track if product section is in view
          // JL mobile: the root is extended far below the viewport, so the main button
          // counts as "intersecting" while visible or still below; the bar shows once it is above
          this.shouldHideForProduct = entry.isIntersecting;
          this.updateVisibility();
        });
      },
      {
        // Trigger when the product section is completely out of view
        threshold: 0,
        rootMargin: this.jlMobileTrigger ? '0px 0px 100000px 0px' : '0px'
      }
    );

    // Start observing the product section
    this.observer.observe(this.observedEl);

    // Create Intersection Observer for footer (if footer exists)
    if (this.footerWrapper) {
      this.footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Hide when footer is at least 25% in view
            this.shouldHideForFooter = entry.isIntersecting;
            this.updateVisibility();
          });
        },
        {
          // Trigger when 25% of the footer is visible
          threshold: 0.25,
          rootMargin: '0px'
        }
      );

      // Start observing the footer
      this.footerObserver.observe(this.footerWrapper);
    }
  }

  updateVisibility() {
    // Hide if either product section is in view OR footer is at least 25% in view
    if (this.shouldHideForProduct || this.shouldHideForFooter) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    // Clear any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    
    // Remove hiding class if present and add visible class
    this.classList.remove('is-hiding');
    this.classList.add('is-visible');
    // JL: lets bottom widgets (loyalty launcher, back-to-top) move out of the way
    document.documentElement.classList.add('jl-satc-on');
  }

  hide() {
    document.documentElement.classList.remove('jl-satc-on');
    // Don't hide if not visible
    if (!this.classList.contains('is-visible')) {
      return;
    }
    
    // Start hide animation
    this.classList.remove('is-visible');
    this.classList.add('is-hiding');
    
    // Wait for animation to complete before removing is-hiding class
    this.hideTimeout = setTimeout(() => {
      this.classList.remove('is-hiding');
      this.hideTimeout = null;
    }, 300); // Match animation duration
  }

  cleanup() {
    document.documentElement.classList.remove('jl-satc-on');
    // Clear any pending timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    
    // Disconnect product section observer and clean up references
    if (this.observer && (this.observedEl || this.productSection)) {
      this.observer.unobserve(this.observedEl || this.productSection);
      this.observer.disconnect();
    }
    
    // Disconnect footer observer and clean up references
    if (this.footerObserver && this.footerWrapper) {
      this.footerObserver.unobserve(this.footerWrapper);
      this.footerObserver.disconnect();
    }
    
    this.observer = null;
    this.footerObserver = null;
    this.productSection = null;
    this.observedEl = null;
    this.footerWrapper = null;
  }
}

// Register the custom element
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
/******/ })()
;